import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export type ToastType = 'success' | 'error' | 'info';

export interface ToastMessage {
    id: string;
    message: ReactNode;
    type: ToastType;
    onClick?: () => void;
}

interface ToastContextType {
    showToast: (message: ReactNode, type?: ToastType, onClick?: () => void) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

const ToastItem: React.FC<{ 
    toast: ToastMessage, 
    onClose: (id: string) => void, 
    isLight: boolean, 
    animationName: string, 
    transformOrigin: string 
}> = ({ toast, onClose, isLight, animationName, transformOrigin }) => {
    const [timeLeft, setTimeLeft] = useState(7);
    const [isHovered, setIsHovered] = useState(false);
    const [isPinned, setIsPinned] = useState(false);

    useEffect(() => {
        if (isHovered || isPinned) return; // Pause timer

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    onClose(toast.id);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isHovered, isPinned, onClose, toast.id]);

    let bgColor = isLight ? '#ffffff' : '#27272a';
    let borderColor = isLight ? '#e5e7eb' : '#3f3f46';
    let iconColor = '#3b82f6'; // info

    if (toast.type === 'error') {
        borderColor = '#ef4444';
        iconColor = '#ef4444';
    } else if (toast.type === 'success') {
        borderColor = '#10b981';
        iconColor = '#10b981';
    }

    const showX = isHovered || isPinned;

    return (
        <div 
            onClick={() => {
                setIsPinned(true);
                if (toast.onClick) toast.onClick();
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            style={{
                pointerEvents: 'auto',
                padding: '1rem',
                background: bgColor,
                border: `1px solid ${borderColor}`,
                borderLeft: `4px solid ${iconColor}`,
                borderRadius: '8px',
                boxShadow: isLight 
                    ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' 
                    : '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.25)',
                color: isLight ? '#1f2937' : '#f4f4f5',
                transition: 'all 0.2s ease',
                position: 'relative',
                animation: `${animationName} 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards`,
                transformOrigin,
                fontSize: '0.9rem',
                fontWeight: 500,
                wordBreak: 'break-word',
                whiteSpace: 'pre-wrap',
                paddingRight: '2.5rem', // space for top-right object
            }}
        >
            {/* The Unified Close/Countdown Button */}
            <div 
                onClick={(e) => {
                    e.stopPropagation();
                    onClose(toast.id);
                }}
                style={{
                    position: 'absolute',
                    top: '8px',
                    right: '8px',
                    cursor: showX ? 'pointer' : 'default',
                    opacity: showX ? 0.6 : 0.9,
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    background: showX ? (isLight ? '#f3f4f6' : '#3f3f46') : 'transparent',
                    color: showX ? (isLight ? '#1f2937' : '#f4f4f5') : iconColor,
                    transition: 'background-color 0.2s ease, color 0.2s ease, opacity 0.2s ease'
                }}
                onMouseEnter={e => { if (showX) e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { if (showX) e.currentTarget.style.opacity = '0.6'; }}
            >
                <svg
                    width="24"
                    height="24"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        transform: 'rotate(-90deg)',
                        opacity: showX ? 0 : 1,
                        transition: 'opacity 0.2s ease',
                        pointerEvents: 'none'
                    }}
                >
                    <circle
                        cx="12"
                        cy="12"
                        r="10"
                        fill="transparent"
                        stroke={iconColor}
                        strokeWidth="2"
                        strokeDasharray="63"
                        style={{
                            animation: showX ? 'none' : 'drawCircle 1s linear infinite'
                        }}
                    />
                </svg>

                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    opacity: showX ? 0 : 1,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none'
                }}>
                    {timeLeft}
                </div>

                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1rem',
                    fontWeight: 'normal',
                    lineHeight: 1,
                    opacity: showX ? 1 : 0,
                    transition: 'opacity 0.2s ease',
                    pointerEvents: 'none'
                }}>
                    &times;
                </div>
            </div>

            {toast.message}
        </div>
    );
};

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);
    const [isLight, setIsLight] = useState(false);

    useEffect(() => {
        const checkTheme = () => {
            try {
                const clientTheme = localStorage.getItem('crm-theme');
                const prefersLight = window.matchMedia('(prefers-color-scheme: light)').matches;
                setIsLight(clientTheme ? clientTheme === 'light' : prefersLight);
            } catch {
                setIsLight(false);
            }
        };
        
        checkTheme();
        window.addEventListener('storage', checkTheme);
        const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
        mediaQuery.addEventListener('change', checkTheme);
        
        return () => {
            window.removeEventListener('storage', checkTheme);
            mediaQuery.removeEventListener('change', checkTheme);
        };
    }, []);

    const showToast = useCallback((message: ReactNode, type: ToastType = 'success', onClick?: () => void) => {
        setToasts(prev => {
            if (prev.some(t => t.message === message && t.type === type)) {
                return prev;
            }
            const id = Math.random().toString(36).substring(2, 9);
            return [...prev, { id, message, type, onClick }];
        });
    }, []);

    const removeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const topCenterToasts = toasts.filter(t => t.type === 'error' || t.type === 'info');
    const bottomRightToasts = toasts.filter(t => t.type !== 'error' && t.type !== 'info');

    return (
        <ToastContext.Provider value={{ showToast }}>
            {children}
            <style>
                {`
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(50px) scale(0.95); }
                    to { opacity: 1; transform: translateX(0) scale(1); }
                }
                @keyframes slideInDown {
                    from { opacity: 0; transform: translateY(-50px) scale(0.95); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes drawCircle {
                    0% { stroke-dashoffset: 63; }
                    100% { stroke-dashoffset: 0; }
                }
                `}
            </style>
            
            {/* Top Center Toasts (Error, Info) */}
            <div style={{
                position: 'fixed',
                top: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 20000,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                pointerEvents: 'none',
                width: '100%',
                maxWidth: '450px'
            }}>
                {topCenterToasts.map(toast => (
                    <ToastItem 
                        key={toast.id}
                        toast={toast}
                        onClose={removeToast}
                        isLight={isLight}
                        animationName="slideInDown"
                        transformOrigin="top center"
                    />
                ))}
            </div>

            {/* Bottom Right Toasts (Success) */}
            <div style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 20000,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                pointerEvents: 'none',
                maxWidth: '350px'
            }}>
                {bottomRightToasts.map(toast => (
                    <ToastItem 
                        key={toast.id}
                        toast={toast}
                        onClose={removeToast}
                        isLight={isLight}
                        animationName="slideInRight"
                        transformOrigin="right center"
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};
